import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Shield, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LAZ_LOGO_URL } from "@/components/LazLogo";

function NavItem({ to, icon: Icon, label, active, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-300 ${
        active
          ? "bg-amber-700 text-stone-50 shadow-sm"
          : "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
      }`}
    >
      <Icon size={18} strokeWidth={1.5} />
      <span>{label}</span>
    </Link>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const isActive = (p) => path === p || (p !== "/" && path.startsWith(p));

  const initials =
    user?.name
      ?.split(" ")
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  const homePath = user?.role === "admin" ? "/admin" : "/dashboard";

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-stone-200 bg-stone-50/80 backdrop-blur-xl sticky top-0 h-screen p-4 gap-2">
        <Link
          to={homePath}
          className="flex items-center gap-2 px-2 py-4"
          data-testid="sidebar-logo"
        >
          <div className="h-10 w-10 rounded-2xl bg-white border border-stone-200 overflow-hidden shadow-sm">
            <img
              src={LAZ_LOGO_URL}
              alt="LAZ Coach"
              className="h-full w-full object-cover"
              draggable="false"
            />
          </div>
          <div>
            <div className="font-serif text-xl text-stone-900 leading-none">LAZ Coach</div>
            <div className="text-[10px] tracking-[0.2em] text-stone-500 uppercase">
              {user?.role === "admin" ? "Owner Studio" : "Client Portal"}
            </div>
          </div>
        </Link>

        <div className="flex flex-col gap-1 mt-2">
          {user?.role === "admin" ? (
            <NavItem
              to="/admin"
              icon={Shield}
              label="Owner Dashboard"
              active={isActive("/admin")}
              testid="nav-admin"
            />
          ) : (
            <NavItem
              to="/dashboard"
              icon={LayoutDashboard}
              label="My Dashboard"
              active={isActive("/dashboard")}
              testid="nav-dashboard"
            />
          )}
          <NavItem
            to="/settings"
            icon={Settings}
            label="Settings"
            active={isActive("/settings")}
            testid="nav-settings"
          />
        </div>

        <div className="mt-auto border-t border-stone-200 pt-4 space-y-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.avatar_url || undefined} />
              <AvatarFallback className="bg-amber-700 text-stone-50 text-xs">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div
                data-testid="sidebar-user-name"
                className="text-sm text-stone-900 truncate"
              >
                {user?.name}
              </div>
              <div className="text-[11px] text-stone-500 truncate">
                {user?.role === "admin" ? "Administrator" : "Client"}
              </div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-stone-600 hover:bg-stone-100 hover:text-stone-900 rounded-xl"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
          >
            <LogOut size={16} strokeWidth={1.5} className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 bg-stone-50/85 backdrop-blur-xl border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <Link to={homePath} className="flex items-center gap-2" data-testid="topbar-logo">
          <div className="h-8 w-8 rounded-xl bg-white border border-stone-200 overflow-hidden">
            <img
              src={LAZ_LOGO_URL}
              alt="LAZ Coach"
              className="h-full w-full object-cover"
              draggable="false"
            />
          </div>
          <span className="font-serif text-lg">LAZ Coach</span>
        </Link>
        <Link to="/settings" data-testid="topbar-profile">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.avatar_url || undefined} />
            <AvatarFallback className="bg-amber-700 text-stone-50 text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Link>
      </header>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <div className="max-w-6xl mx-auto p-6 md:p-10">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-stone-50/95 backdrop-blur-xl border-t border-stone-200 flex justify-around py-2">
        {user?.role === "admin" ? (
          <Link to="/admin" className="p-2 text-stone-600" data-testid="mob-nav-admin">
            <Shield size={20} strokeWidth={1.5} />
          </Link>
        ) : (
          <Link to="/dashboard" className="p-2 text-stone-600" data-testid="mob-nav-dashboard">
            <LayoutDashboard size={20} strokeWidth={1.5} />
          </Link>
        )}
        <Link to="/settings" className="p-2 text-stone-600" data-testid="mob-nav-settings">
          <Settings size={20} strokeWidth={1.5} />
        </Link>
      </nav>
    </div>
  );
}
